import type {
  ColDef,
  IAggFuncParams,
  IAggFuncResult,
  ICellRendererParams,
  ValueGetterFunc,
} from "ag-grid-community"
import type { ReactNode } from "react"

import { getFilteredLeafData } from "~/utils/ag-utils"
import { isProduction } from "~/utils/environment"

import type { ColumnNamingId } from "../utils/columnUtils"

/**
 * One column as a self-contained unit: id, colDef builder, and value math.
 *
 * Where a column's inputs live:
 * - Per-row inputs are row data, so a changed row re-aggregates only its own group path.
 * - Table-wide inputs that change while the table is open (warehouse, forecasts) are AG Grid's
 *   grid context, typed as `GridCtx`: the slots read it as `grid`, `colDef` callbacks as
 *   `params.context`. The table repaints the columns that read it when it changes; their
 *   definitions stay as they are.
 * - `ctx` holds structure only: flags, editability, currency, header inputs. A `ctx` change
 *   rebuilds the module's definitions, so data never goes there.
 * - A group whose value is not the aggregation of its leaf values uses `combine`: `calculate`
 *   returns an `IAggFuncResult` carrying the parts, and each group adds up its direct children's
 *   results, so no group re-reads its leaves.
 *
 * The slots are advisory. Logic they cannot express stays inside `colDef`: a value getter that
 * branches on `params.node.group`, an aggregation over `params.values` rather than the leaf rows,
 * a comparator that reads the nodes. A column resolving its leaf value through `colDef.field`
 * needs no `calculate`: a value getter changes its filtering, editing, export and inferred cell
 * data type.
 *
 * A spec never fetches. A loading state belongs in the value a column carries, never on the
 * definition: a definition that changes identity refreshes its column.
 */
export interface ColumnSpec<Row, Ctx = void, GridCtx = void> {
  colId: ColumnNamingId
  /** `ctx` carries the table structure, so the definition closes over nothing. */
  colDef: (ctx: Ctx) => ColDef<Row>
  /**
   * Leaf-row value, wired to `colDef.valueGetter` by {@link resolveColumnSpecs}. AG Grid calls it
   * synchronously for rendering, sorting, filtering, aggregating and exporting, so it reads
   * already-resolved data; rows without data (group and footer rows) read `undefined`.
   */
  calculate?: (row: Row, ctx: Ctx, grid: GridCtx) => unknown
  /**
   * Group-row value from the group's leaf rows, where group math is not the aggregation of leaf
   * math. Replaces the factory's built-in `aggFunc` ("sum", "avg", "unique").
   */
  aggregate?: (leaves: Row[], ctx: Ctx, grid: GridCtx) => unknown
  /**
   * Group-row value from the values of the group's direct children that pass the filter: leaf
   * results from `calculate`, sub-group results from `combine` itself.
   */
  combine?: (children: (IAggFuncResult | null | undefined)[], ctx: Ctx) => IAggFuncResult
  /**
   * Sort comparison, for columns ordered by something other than their cell value. `isDescending`
   * is the grid's current direction, for columns that order unknowns last in both directions.
   */
  compare?: (a: unknown, b: unknown, ctx: Ctx, isDescending: boolean) => number
  /** Explicitly non-pure: renderers may open dialogs and fire mutations. */
  render?: (params: ICellRendererParams<Row, any, GridCtx>, ctx: Ctx) => ReactNode
}

/**
 * Builds the column definitions for a set of specs, in array order. That order reaches the grid
 * only for a module the caller splices in whole; a module read through
 * {@link resolveColumnSpecsById} is ordered by the caller's lookup sequence instead, and its
 * array order carries no meaning.
 */
export function resolveColumnSpecs<Row, Ctx, GridCtx>(
  specs: readonly ColumnSpec<Row, Ctx, GridCtx>[],
  ctx: Ctx,
): ColDef<Row>[] {
  return specs.map((spec) => {
    const { calculate, aggregate, combine, compare, render } = spec
    const wired: ColDef<Row> = { ...spec.colDef(ctx) }

    if (calculate) {
      assertSlotIsFree(spec, wired, "valueGetter", "calculate")
      // `data` is absent on group rows, where the aggregation supplies the value instead.
      const valueGetter: ValueGetterFunc<Row> = (params) =>
        params.data ? calculate(params.data, ctx, params.context) : undefined
      wired.valueGetter = valueGetter
    }
    if (aggregate) {
      assertSlotIsFree(spec, wired, "aggFunc", "aggregate")
      wired.aggFunc = (params: IAggFuncParams<Row>) =>
        aggregate(getFilteredLeafData(params.rowNode), ctx, params.context)
    }
    if (combine) {
      assertSlotIsFree(spec, wired, "aggFunc", "combine")
      wired.aggFunc = (params: IAggFuncParams<Row>) => combine(params.values, ctx)
      // AG Grid unwraps an `IAggFuncResult` for its own number formatter on group rows only. Here
      // leaves carry one too, and the provided filters and the factories' formatters read the raw
      // cell value.
      assertSlotIsFree(spec, wired, "filterValueGetter", "combine")
      wired.filterValueGetter = (params) => unwrapAggResult(params.getValue(spec.colId))
      const format = wired.valueFormatter
      if (typeof format === "function") {
        wired.valueFormatter = (params) =>
          format({ ...params, value: unwrapAggResult(params.value) })
      } else {
        failSlotCheck(spec, COMBINE_FORMATTER_HINT)
      }
    }
    if (compare) {
      assertSlotIsFree(spec, wired, "comparator", "compare")
      wired.comparator = (a, b, _nodeA, _nodeB, isDescending) => compare(a, b, ctx, isDescending)
    }
    if (render) {
      assertSlotIsFree(spec, wired, "cellRenderer", "render")
      wired.cellRenderer = (params: ICellRendererParams<Row, any, GridCtx>) => render(params, ctx)
    }

    return wired
  })
}

const unwrapAggResult = (value: unknown) =>
  typeof (value as IAggFuncResult | null)?.toNumber === "function"
    ? (value as IAggFuncResult).toNumber!()
    : value

type SlotKey = "valueGetter" | "aggFunc" | "filterValueGetter" | "comparator" | "cellRenderer"

const SLOT_HINTS: Record<SlotKey, string> = {
  valueGetter:
    "Move the getter's logic into `calculate`, or drop `calculate` if it needs the grid params.",
  aggFunc:
    "Move the function's logic into `aggregate` or `combine`, or drop the slot if it needs " +
    "`params.values`.",
  filterValueGetter:
    "Drop the colDef's `filterValueGetter`: `combine` installs one that filters on the result's " +
    "number.",
  comparator:
    "If it is createStringColumn's default, pass `suppressDefaultComparator: true` in the colDef; " +
    "otherwise move its logic into `compare`, or drop `compare` if it needs the nodes.",
  cellRenderer:
    "Move the renderer into `render`, or drop `render` if the definition needs a component class.",
}

/**
 * Enabled everywhere but production — locally, in tests, and on the dev, staging, demo and
 * feature-branch hosts. Gated on the runtime hostname like `renderRegistry`, since
 * `import.meta.env.DEV` is not a reliable production guard in client chunks.
 */
const SLOT_CHECKS_ENABLED = typeof window === "undefined" || !isProduction()

const COMBINE_FORMATTER_HINT =
  "defines `combine` without a function `valueFormatter`. Its cells hold an `IAggFuncResult`, " +
  "so the column needs a formatter function to receive the result's number."

/**
 * A spec expresses each piece of a column's behaviour once: either in a slot or inside `colDef`.
 * Wiring a slot over a definition that already fills the same key would drop the definition's
 * version silently, so it fails loudly outside production. A built-in `aggFunc` name such as
 * "sum" is what the column factories fill in by default and carries no logic, so `aggregate` may
 * replace it.
 */
function assertSlotIsFree<Row, Ctx, GridCtx>(
  spec: ColumnSpec<Row, Ctx, GridCtx>,
  colDef: ColDef<Row>,
  key: SlotKey,
  slot: "calculate" | "aggregate" | "combine" | "compare" | "render",
): void {
  const existing = colDef[key]
  if (existing == null) return
  if (key === "aggFunc" && typeof existing === "string") return
  failSlotCheck(
    spec,
    `defines \`${slot}\` in its spec and also \`${key}\` in its colDef. ` +
      `The slot is wired onto \`colDef.${key}\`, so the colDef's version would be dropped ` +
      `silently. Keep one. ${SLOT_HINTS[key]}`,
  )
}

function failSlotCheck<Row, Ctx, GridCtx>(spec: ColumnSpec<Row, Ctx, GridCtx>, problem: string) {
  if (!SLOT_CHECKS_ENABLED) return
  const message = `Column "${spec.colId}" ${problem}`
  // Logged as well as thrown: the error card shows generic text, so this is where the detail lands.
  console.error(message)
  throw new Error(message)
}

/**
 * Resolves a set of specs once into a lookup by colId, for a domain whose columns are not
 * contiguous: the caller places each at its own position, all from one memo. Throws on an unknown
 * colId — `ColumnNamingId` is too wide a union for the compiler to catch it.
 */
export function resolveColumnSpecsById<Row, Ctx, GridCtx>(
  specs: readonly ColumnSpec<Row, Ctx, GridCtx>[],
  ctx: Ctx,
): (colId: ColumnNamingId) => ColDef<Row> {
  const colDefs = resolveColumnSpecs(specs, ctx)
  const resolved = new Map<ColumnNamingId, ColDef<Row>>()
  specs.forEach((spec, index) => resolved.set(spec.colId, colDefs[index]!))
  return (colId) => {
    const colDef = resolved.get(colId)
    if (!colDef) throw new Error(`No column spec registered for colId "${colId}"`)
    return colDef
  }
}
